import type {
  MfaPolicy,
  ProjectBudgetMode,
  ProjectRevenueState,
  TenantStatus,
  UserRole,
} from '@inou/shared';

/**
 * API クライアント。
 *
 * セッションは HttpOnly Cookie なので credentials: 'include' だけで通る。
 * 状態を変える要求には Origin が必要だが、同一オリジンならブラウザが自動で付ける。
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(res.status, body?.error ?? `リクエストに失敗しました (${res.status})`);
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** 削除対象をボディで指定する場合 (リポジトリの紐付け解除など) */
  deleteWithBody: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'DELETE', body: JSON.stringify(body) }),
};

export interface Project {
  id: string;
  name: string;
  /** 種別。未設定なら null。 */
  projectTypeId: string | null;
  projectTypeName: string | null;
  isCapitalizable: boolean;
  status: 'active' | 'archived';
  /** 予算の時間軸。金額は /api/projects/:id/budgets が持つ。 */
  budgetMode: ProjectBudgetMode;
  mcpCustomInstruction: string | null;
  repositories: string[];
}

/** プロジェクトの予算 (円)。通期は month が null。 */
export interface ProjectBudget {
  month: string | null;
  costBudget: number | null;
  revenueBudget: number | null;
  note: string | null;
}

export interface ProjectBudgets {
  budgetMode: ProjectBudgetMode;
  /** 通期の 1 本。未設定なら null。 */
  total: ProjectBudget | null;
  /** 月次の行。month の昇順。 */
  monthly: ProjectBudget[];
}

/**
 * プロジェクト種別。テナント設定で管理するマスタ。
 *
 * `defaultBillable` は工数の請求可能既定、`countsAsDirect` は稼働レポートの
 * 直接工数の数え方を決める。資産化の可否はプロジェクト側の設定。
 */
export interface ProjectType {
  id: string;
  name: string;
  defaultBillable: boolean;
  countsAsDirect: boolean;
  status: 'active' | 'archived';
  /** この種別を使っているプロジェクト数。0 のときだけ削除できる。 */
  projectCount: number;
}

export interface Member {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: 'active' | 'invited' | 'disabled';
  hourlyCost: number | null;
  hourlyRate: number | null;
  invitationExpiresAt: number | null;
}

/** 種別ごとのライセンス枠 */
export interface LicenseSeats {
  activeUsers: number;
  purchasedSeats: number;
  grantedSeats: number;
  availableSeats: number;
  remainingSeats: number;
}

/** mcp = MCP Connect / full = Full Management */
export interface SeatUsage {
  mcp: LicenseSeats;
  full: LicenseSeats;
}

/**
 * IP 許可リストの 1 行。**空 = 制限なし**なので、0 件と「未設定」は区別しない。
 * cidr はサーバーが正規化した形 (203.0.113.0/24)。
 */
export interface IpAllowlistEntry {
  id: string;
  cidr: string;
  label: string | null;
  createdAt: number;
}

/** 招待リンク。平文トークンは発行時の応答にしか出ないので、その場で控える。 */
export interface Invitation {
  url: string;
  expiresAt: number;
}

export interface Assignment {
  id: string;
  projectId: string;
  projectName: string;
  projectStatus: 'active' | 'archived';
  userId: string;
  userName: string;
  userStatus: 'active' | 'invited' | 'disabled';
  month: string;
  plannedHours: number;
  customHourlyCost: number | null;
  note: string | null;
  actualHours: number;
}

export interface TenantSettings {
  id: string;
  name: string;
  status: TenantStatus;
  /** 削除予定日 (Unix 秒)。pending_deletion のときだけ入る。 */
  deletionScheduledAt: number | null;
  timezone: string;
  defaultHourlyCost: number;
  defaultHourlyRate: number;
  mcpCustomInstruction: string | null;
  featureFlags: Record<string, boolean>;
  /** 二段階認証を要求する範囲 */
  mfaPolicy: MfaPolicy;
}

/** 第 2 要素を持たない在籍者の数。ポリシーの範囲ごとに数えたもの。 */
export interface MfaUnenrolled {
  admin: number;
  all: number;
}

export type TimeLogCategory = 'development' | 'maintenance' | 'meeting' | 'other';

export interface TimeLog {
  id: string;
  userId: string;
  userName: string;
  projectId: string;
  projectName: string;
  hours: number;
  workedOn: string;
  category: TimeLogCategory;
  isCapitalizedLog: boolean;
  isBillable: boolean;
  note: string | null;
  /** 'mcp' = AI クライアントからの記録 / 'web' = 画面での訂正 */
  source: 'mcp' | 'web';
  createdAt: number;
  updatedAt: number;
}

export interface LoginSession {
  id: string;
  createdAt: number;
  lastUsedAt: number;
  expiresAt: number;
  ip: string | null;
  userAgent: string | null;
  current: boolean;
}

export interface PasskeyCredential {
  id: string;
  label: string | null;
  deviceType: string | null;
  backedUp: boolean;
  createdAt: number;
  lastUsedAt: number | null;
}

// --- レスポンスの型 (API 側の reports/queries.ts と対応) ---

/** MCP 接続用の個人アクセストークン。平文は発行時の応答にしか出ない。 */
export interface ApiToken {
  id: string;
  name: string;
  prefix: string;
  /** null なら無期限 */
  expiresAt: number | null;
  revokedAt: number | null;
  lastUsedAt: number | null;
  createdAt: number;
}

/**
 * OAuth で接続した MCP クライアント 1 件。
 *
 * トークンそのものは出てこない。**利用者が管理するのは接続の単位**で、
 * その裏で回っているアクセストークン (1 時間) とリフレッシュトークンは
 * 見せる意味がない。
 */
export interface OAuthGrant {
  id: string;
  clientName: string;
  clientUri: string | null;
  scope: string;
  createdAt: number;
  lastUsedAt: number | null;
}

export interface Me {
  user: { id: string; name: string; email: string; role: UserRole };
  tenant: { id: string; name: string };
  totpEnabled: boolean;
  passkeyCount: number;
  unusedRecoveryCodes: number;
  /** テナントのポリシーで、自分が二段階認証を要求されているか */
  mfaRequired: boolean;
  /** このセッションがそれを満たしているか。false なら業務 API は 403 */
  mfaSatisfied: boolean;
}

export interface UtilizationMember {
  userId: string;
  name: string;
  role: string;
  plannedHours: number;
  actualHours: number;
  rate: number | null;
  directHours: number;
  indirectHours: number;
  billableHours: number;
  over: boolean;
}

export interface UtilizationReport {
  month: string;
  totals: {
    plannedHours: number;
    actualHours: number;
    rate: number | null;
    overCount: number;
    directHours: number;
    indirectHours: number;
  };
  members: UtilizationMember[];
}

export interface CapitalizationProject {
  projectId: string;
  name: string;
  projectType: string | null;
  actualHours: number;
  capitalizedHours: number;
  expensedHours: number;
  capRate: number | null;
  costAmount: number;
  capitalizedCost: number;
  expensedCost: number;

  /** 請求可能工数。想定売上を出してよいかの判定材料 (種別は見ない)。 */
  billableHours: number;
  /** 想定売上・粗利を数字で出してよいか。`ok` 以外なら下の 2 つは null。 */
  revenueState: ProjectRevenueState;
  /** 請求可能工数のうち、売上単価が解決できなかった分 (穴の大きさ)。 */
  unratedBillableHours: number;
  revenueAmount: number | null;
  grossProfit: number | null;

  /** 予算の時間軸。'none' なら以降の予算フィールドは全部 null。 */
  budgetMode: ProjectBudgetMode;
  costBudget: number | null;
  revenueBudget: number | null;
  /** 予算と突き合わせる実績原価。月次は当月、通期は全期間〜当月の累計。 */
  budgetActualCost: number | null;
  budgetActualRevenue: number | null;
  /** 原価の消化率 (%)。予算が未設定か 0 なら null。 */
  costUsageRate: number | null;
  /** 残 (原価予算 − 消化額)。超過は負。 */
  costRemaining: number | null;
  overBudget: boolean;
}

export interface CapitalizationReport {
  month: string;
  totals: {
    actualHours: number;
    capitalizedHours: number;
    expensedHours: number;
    capRate: number | null;
    costAmount: number;
    capitalizedCost: number;
    expensedCost: number;
    revenueAmount: number | null;
    grossProfit: number | null;
    /**
     * 全社の想定売上・粗利を出してよいか。
     * プロジェクト行と違い、請求可能工数が 1 時間でもあれば出す
     * (自社プロダクトの原価は実際に全社の粗利を押し下げるので合計に含める)。
     */
    revenueState: ProjectRevenueState;
    /** 売上単価が解決できないプロジェクトの数 */
    unresolvedRateProjectCount: number;
    /** 原価予算を超えたプロジェクトの数 */
    overBudgetCount: number;
  };
  projects: CapitalizationProject[];
}

export interface Announcement {
  id: string;
  message: string;
}

/**
 * 監査ログの 1 行。型の正は `@inou/shared` の `AuditLogEntry`
 * (API の応答と画面が同じものを見る)。
 */
/**
 * 開通直後の案内の進捗。済み / 未済は API が実データから導出したもので、
 * DB に完了フラグは無い (型の正は `@inou/shared`)。
 */
export type {
  AuditActorType,
  AuditChanges,
  AuditLogEntry,
  AuditTargetType,
  OnboardingStatus,
  OnboardingStep,
  OnboardingStepId,
} from '@inou/shared';

// --- 運営パネル (Cloudflare Access の背後) ---

export interface Operator {
  email: string;
  subject: string;
}

export interface HqTenant {
  id: string;
  name: string;
  status: TenantStatus;
  /** 削除予定日 (Unix 秒)。pending_deletion のときだけ入る。 */
  deletionScheduledAt: number | null;
  seats: {
    mcp: Omit<LicenseSeats, 'remainingSeats'>;
    full: Omit<LicenseSeats, 'remainingSeats'>;
  };
  featureFlags: Record<string, boolean>;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  /** 受諾前の管理者しか居ない = まだ誰もログインできていないテナント */
  pendingAdminEmail: string | null;
  createdAt: number;
}

export interface HqAnnouncement {
  id: string;
  message: string;
  activeFrom: number;
  activeTo: number | null;
  createdAt: number;
  updatedAt: number;
}
